/**
 * DefaultSeo — per-route default <head> SEO for every page that does NOT render
 * its own <SeoHead> (i.e. everything except /resources*). Renders via React 19
 * native document-metadata hoisting. Unique title/description/OG per marketing
 * route (ROUTE_META), plus site-wide Organization + WebSite JSON-LD.
 */
const SITE = "https://netlift.app";
const DEFAULT_TITLE = "NetLift | Save Time. Show Return.";
const DEFAULT_DESCRIPTION =
  "AI Value Management for measuring the cost, savings, and return of AI adoption. " +
  "Track or import AI-assisted work, compare time with and without AI, and show the return.";

// Per-route unique meta. Keys are exact pathnames.
const ROUTE_META = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    ogTitle: "NetLift — AI Value Management",
    ogDescription: "Measure the cost, savings, and return of AI adoption. Save time, show return.",
  },
  "/pricing": {
    title: "Pricing | NetLift — AI Value Management Plans",
    description:
      "NetLift pricing: Pilot, Growth and Business plans to measure AI ROI. 14-day free trial, no credit card. Compare time with and without AI and show the return.",
    ogTitle: "NetLift Pricing — Plans to Measure AI ROI",
    ogDescription: "Pilot, Growth and Business plans. 14-day free trial, no credit card required.",
  },
  "/integrations": {
    title: "Integrations & API | NetLift",
    description:
      "Bring AI work data into NetLift via native time tracking, CSV import, inbound API, Jira, Azure DevOps and OpenAI usage. See what's live versus planned for measuring AI ROI.",
    ogTitle: "NetLift Integrations & API",
    ogDescription: "Native tracking, CSV, API, Jira, Azure DevOps and OpenAI usage — clearly labelled live vs planned.",
  },
  "/demo": {
    title: "Sample AI Value Report | NetLift",
    description:
      "See a sample NetLift AI Value Report — time saved, current net value, future value, payback and Evidence Quality across teams and AI tools. Built on demo data.",
    ogTitle: "Sample AI Value Report — NetLift",
    ogDescription: "Time saved, net value, future value, payback and Evidence Quality on sample data.",
  },
  "/contact": {
    title: "Contact | NetLift",
    description:
      "Contact the NetLift team about measuring the cost, savings and return of AI adoption for your organisation.",
    ogTitle: "Contact NetLift",
    ogDescription: "Talk to us about measuring the return on your AI adoption.",
  },
  "/privacy": {
    title: "Privacy Policy | NetLift",
    description:
      "NetLift privacy policy — how we handle your data. NetLift is built for AI value reporting, not individual productivity scoring or surveillance.",
  },
  "/terms": {
    title: "Terms of Use | NetLift",
    description:
      "NetLift terms of use. NetLift measures AI value across work, workflows and tools for business reporting, not employee surveillance.",
  },
};

export function DefaultSeo({ pathname = "/" }) {
  let path = pathname || "/";
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  const url = SITE + path;
  const known = Boolean(ROUTE_META[path]);
  const m = ROUTE_META[path] || {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  };
  const ogTitle = m.ogTitle || m.title;
  const ogDescription = m.ogDescription || m.description;

  const orgSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": SITE + "/#organization",
        name: "NetLift",
        url: SITE,
        logo: SITE + "/icon-512.png",
        description: "AI Value Management for measuring the cost, savings, and return of AI adoption.",
        sameAs: [
          "https://www.linkedin.com/company/netliftapp/",
          "https://www.producthunt.com/products/netlift",
        ],
        contactPoint: { "@type": "ContactPoint", email: "support@netlift.app", contactType: "customer support" },
      },
      {
        "@type": "WebSite",
        "@id": SITE + "/#website",
        name: "NetLift",
        url: SITE,
        publisher: { "@id": SITE + "/#organization" },
      },
    ],
  };

  return (
    <>
      <title>{m.title}</title>
      <meta name="description" content={m.description} />
      {known ? <link rel="canonical" href={url} /> : null}
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="NetLift" />
      <meta property="og:image" content={SITE + "/og-default.jpg"} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={SITE + "/og-default.jpg"} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
    </>
  );
}
